import { emit, listen } from "@tauri-apps/api/event";
import { isTauri } from "@/lib/is-tauri";

export interface PresentationGuideCommand {
  presentationId: string;
  stepId: string;
  action: "pause" | "resume" | "repeat" | "continue" | "cancel" | "detected";
}
const EVENT = "denker://presentation-guide-command";
const listeners = new Set<(command: PresentationGuideCommand) => void>();
const seen = new Set<string>();
const retained = new Map<
  string,
  { paused: boolean; cancelled: boolean; completed: Set<string> }
>();
let ready: Promise<unknown> | undefined;

function receive(payload: unknown) {
  if (!payload || typeof payload !== "object") return;
  const value = payload as Record<string, unknown>;
  if (
    typeof value.id !== "string" ||
    seen.has(value.id) ||
    typeof value.presentationId !== "string" ||
    typeof value.stepId !== "string" ||
    !["pause", "resume", "repeat", "continue", "cancel", "detected"].includes(
      String(value.action),
    )
  )
    return;
  seen.add(value.id);
  if (seen.size > 128) seen.delete(seen.values().next().value!);
  const command = value as unknown as PresentationGuideCommand;
  const state = retained.get(command.presentationId) ?? {
    paused: false,
    cancelled: false,
    completed: new Set<string>(),
  };
  if (command.action === "pause") state.paused = true;
  if (command.action === "resume") state.paused = false;
  if (command.action === "cancel") state.cancelled = true;
  if (
    !state.paused &&
    (command.action === "continue" || command.action === "detected")
  )
    state.completed.add(command.stepId);
  if (command.action === "repeat") state.completed.delete(command.stepId);
  retained.set(command.presentationId, state);
  if (retained.size > 32) retained.delete(retained.keys().next().value!);
  for (const listener of listeners)
    listener(value as unknown as PresentationGuideCommand);
}

export function subscribePresentationGuideCommands(
  listener: (command: PresentationGuideCommand) => void,
  scope: { presentationId: string; stepId: string },
): () => void {
  listeners.add(listener);
  const state = retained.get(scope.presentationId);
  if (state?.completed.has(scope.stepId))
    listener({ ...scope, action: "detected" });
  if (state?.paused) listener({ ...scope, action: "pause" });
  if (state?.cancelled) listener({ ...scope, action: "cancel" });
  if (isTauri() && !ready) {
    ready = listen(EVENT, (event) => receive(event.payload)).catch(() => {
      ready = undefined;
    });
  }
  return () => {
    listeners.delete(listener);
  };
}

export function sendPresentationGuideCommand(
  command: PresentationGuideCommand,
): void {
  const payload = { ...command, id: crypto.randomUUID() };
  receive(payload);
  if (isTauri()) void emit(EVENT, payload).catch(() => {});
}

/** Used when a fresh test/isolated guide environment is torn down. */
export function resetPresentationGuideCommandsForTests(): void {
  retained.clear();
  seen.clear();
}
